import { addParams } from 'helpers/RequestHelper';
import { handleResponse } from 'utilities';
import { getRequestOptions, token } from './ServiceHelper';
import {
  EmissionsStatusEnum,
  LibrariesResult,
  Library,
  LibraryDetailsResult,
  LibraryVersionDetailsResult,
  LibraryVersionFactorsResult,
  LibraryVersionPayload,
  UploadFileDetails,
} from './types';

const API_ENV = process.env.REACT_APP_API_ENV || '';
const baseUrl = `${API_ENV}/emissions/admin`;

export interface PaidEmissionLibrarySubscription {
  libraryId: string;
  label: string;
  description: string;
  region: string;
  status: number;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  updatedOn: string | null;
}

export interface PaidEmissionLibrarySubscriptionUpdate {
  status: number;
  effectiveFrom: string | null;
  effectiveTo: string | null;
}

export function getPaidEmissionLibrarySubscriptions(accountId: string) {
  if (token() != null) {
    const requestOptions = getRequestOptions();
    return fetch(`${baseUrl}/subscriptions/${accountId}`, requestOptions)
      .then(handleResponse)
      .then((result: { subscriptions: PaidEmissionLibrarySubscription[] }) => result.subscriptions);
  }
  return Promise.reject('unauthorised');
}

export function updatePaidEmissionLibrarySubscription(
  accountId: string,
  libraryId: string,
  payload: PaidEmissionLibrarySubscriptionUpdate
) {
  if (token() != null) {
    const requestOptions = getRequestOptions('PUT', payload);
    return fetch(`${baseUrl}/subscriptions/${accountId}/${libraryId}`, requestOptions).then(handleResponse);
  }
  return Promise.reject('unauthorised');
}

/* Emission Libraries */
export function getLibraries() {
  if (token() != null) {
    const requestOptions = getRequestOptions();
    const url = `${baseUrl}/library`;

    return fetch(url, requestOptions)
      .then(handleResponse)
      .then((result: LibrariesResult) => result.libraries);
  } else {
    //No token available
    return Promise.reject('unauthorised');
  }
}

export function createLibrary(payload: Library) {
  if (token() != null) {
    const requestOptions = getRequestOptions('POST', payload);
    const url = `${baseUrl}/library`;

    return fetch(url, requestOptions).then(handleResponse);
  } else {
    //No token available
    return Promise.reject('unauthorised');
  }
}

export function updateLibrary(payload: Library) {
  if (token() != null) {
    const requestOptions = getRequestOptions('PUT', payload);
    const url = `${baseUrl}/library/${payload.libraryId}`;

    return fetch(url, requestOptions).then(handleResponse);
  } else {
    //No token available
    return Promise.reject('unauthorised');
  }
}

/* Library Details */
export function getLibraryDetails(libraryId: string) {
  if (token() != null) {
    const requestOptions = getRequestOptions();
    const url = `${baseUrl}/library/${libraryId}`;

    return fetch(url, requestOptions)
      .then(handleResponse)
      .then((result: LibraryDetailsResult) => result.library);
  } else {
    //No token available
    return Promise.reject('unauthorised');
  }
}

export function getLibraryVersionDetails(libraryId: string, versionId: string) {
  if (token() != null) {
    const requestOptions = getRequestOptions();
    const url = `${baseUrl}/library/${libraryId}/versions/${versionId}`;

    return fetch(url, requestOptions)
      .then(handleResponse)
      .then((result: LibraryVersionDetailsResult) => result.version);
  } else {
    //No token available
    return Promise.reject('unauthorised');
  }
}

export function getFactorsByLibraryVersion(
  libraryId: string,
  versionId: string,
  page: number = 1,
  size: number = 20,
  sort: string = 'label',
  direction: string = 'asc',
  search?: string,
  status?: string,
  signal?: AbortSignal
) {
  if (token() != null) {
    const requestOptions = getRequestOptions('GET', null, 'application/json', signal);
    const url = addParams(`${baseUrl}/library/${libraryId}/versions/${versionId}/factors`, {
      page,
      size,
      sort,
      direction,
      query: search,
      status,
    });

    return fetch(url, requestOptions)
      .then(handleResponse)
      .then((result: LibraryVersionFactorsResult) => result);
  } else {
    //No token available
    return Promise.reject('unauthorised');
  }
}

export function exportFactorsByLibraryVersion(
  libraryId: string,
  versionId: string,
  sort: string = 'label',
  direction: string = 'asc',
  search?: string,
  status?: string
) {
  if (token() != null) {
    const requestOptions = getRequestOptions();
    const url = addParams(`${baseUrl}/library/${libraryId}/versions/${versionId}/factors/export`, {
      sort,
      direction,
      query: search,
      status,
    });

    return fetch(url, requestOptions);
  } else {
    //No token available
    return Promise.reject('unauthorised');
  }
}

export function updateLibraryVersion(libraryId: string, versionId: string, payload: LibraryVersionPayload) {
  if (token() != null) {
    const requestOptions = getRequestOptions('PUT', payload);
    const url = `${baseUrl}/library/${libraryId}/versions/${versionId}`;

    return fetch(url, requestOptions).then(handleResponse);
  } else {
    //No token available
    return Promise.reject('unauthorised');
  }
}

export function updateLibraryVersionStatus(libraryId: string, versionId: string, status: EmissionsStatusEnum) {
  if (token() != null) {
    const requestOptions = getRequestOptions('PATCH');
    const url = addParams(`${baseUrl}/library/${libraryId}/versions/${versionId}`, {
      status,
    });

    return fetch(url, requestOptions).then(handleResponse);
  } else {
    //No token available
    return Promise.reject('unauthorised');
  }
}

export function deleteLibraryVersion(libraryId: string, versionId: string) {
  if (token() != null) {
    const requestOptions = getRequestOptions('DELETE');
    const url = `${baseUrl}/library/${libraryId}/versions/${versionId}`;

    return fetch(url, requestOptions).then(handleResponse);
  } else {
    //No token available
    return Promise.reject('unauthorised');
  }
}

export function upsertComments(libraryId: string, versionId: string, factorId: string, comments: string) {
  if (token() != null) {
    const requestOptions = getRequestOptions('POST');
    const url = addParams(`${baseUrl}/library/${libraryId}/versions/${versionId}/factors/${factorId}/notes`, {
      notes: comments,
    });

    return fetch(url, requestOptions).then(handleResponse);
  } else {
    //No token available
    return Promise.reject('unauthorised');
  }
}

/* Upload versions */
export function createLibraryVersion(libraryId: string, payload: LibraryVersionPayload) {
  if (token() != null) {
    const requestOptions = getRequestOptions('POST', payload);
    const url = `${baseUrl}/library/${libraryId}/versions`;

    return fetch(url, requestOptions).then(handleResponse);
  } else {
    //No token available
    return Promise.reject('unauthorised');
  }
}

export function getUploadUrl(libraryId: string, versionId: string, { type, fileName, fileSize }: UploadFileDetails) {
  if (token() != null) {
    const requestOptions = getRequestOptions('POST');
    const url = addParams(`${baseUrl}/library/${libraryId}/versions/${versionId}/upload`, {
      type,
      fileName,
      fileSize,
    });

    return fetch(url, requestOptions).then(handleResponse);
  } else {
    //No token available
    return Promise.reject('unauthorised');
  }
}

export async function uploadToS3(endpoint: string, file: Blob) {
  const requestOptions = {
    method: 'PUT',
    body: file,
  };

  const result = await fetch(`${endpoint}`, requestOptions);
  return result;
}

/* Flat file and Source file */
export type FlatFileType = 'File' | 'DB';

export function getFlatFile(libraryId: string, versionId: string, fileType: FlatFileType) {
  if (token() != null) {
    const requestOptions = getRequestOptions();
    const url = addParams(`${baseUrl}/library/${libraryId}/versions/${versionId}/flat`, {
      type: fileType,
    });

    return fetch(url, requestOptions);
  } else {
    //No token available
    return Promise.reject('unauthorised');
  }
}

export function getSourceFile(libraryId: string, versionId: string) {
  if (token() != null) {
    const requestOptions = getRequestOptions();
    const url = `${baseUrl}/library/${libraryId}/versions/${versionId}/source`;

    return fetch(url, requestOptions);
  } else {
    //No token available
    return Promise.reject('unauthorised');
  }
}

export function seedGlobalEmisisonPreCalculations(accountId: string) {
  if (token() != null) {
    const requestOptions = getRequestOptions('POST');
    const url = `${baseUrl}/${accountId}/administration/seed-groups`;

    return fetch(url, requestOptions).then(handleResponse);
  } else {
    //No token available
    return Promise.reject('unauthorised');
  }
}
